export function parsePositiveAmount(value: string): number | null {
  const numericAmount = parseFloat(value);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) return null;
  return numericAmount;
};

export function parseMoneyInput(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number.parseFloat(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Parses a user-entered currency string into a number of dollars.
 *
 * Intended for form inputs (supports optional "$", commas, and whitespace).
 * Rejects partial junk like "12abc" (returns null).
 */
export function parseCurrencyInputToNumber(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null

  // Allow common input formatting.
  const normalized = trimmed.replace(/[$,\s]/g, '')
  if (!normalized) return null

  // Require the entire string to be numeric (no partial parse).
  // Accept "12", "12.", ".12", "12.3", "12.34" (non-negative only)
  if (!/^(?:\d+|\d*\.\d+|\d+\.\d*)$/.test(normalized)) return null

  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

export function parseIntInput(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number.parseInt(trimmed, 10)
  return Number.isFinite(parsed) ? parsed : null
}

export function parseDate(value: unknown): Date | undefined {
  if (value instanceof Date) return value
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value)
    if (!Number.isNaN(d.getTime())) return d
  }
  return undefined
}

/**
 * Converts an ISO 8601 date-time string to datetime-local input format (YYYY-MM-DDTHH:mm).
 * Used for populating HTML5 datetime-local inputs.
 */
export function formatDateTimeLocalForInput(isoString: string): string {
  if (!isoString || typeof isoString !== 'string') return ''
  const trimmed = isoString.trim()
  if (!trimmed) return ''
  
  const date = new Date(trimmed)
  if (Number.isNaN(date.getTime())) return ''
  
  // Format as YYYY-MM-DDTHH:mm (datetime-local format)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

/**
 * Converts a datetime-local input value (YYYY-MM-DDTHH:mm) to ISO 8601 format.
 * Assumes the input value is in the user's local timezone.
 */
export function parseDateTimeLocalToISO(value: string): string {
  if (!value || typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  
  // datetime-local format: YYYY-MM-DDTHH:mm
  const date = new Date(trimmed)
  if (Number.isNaN(date.getTime())) return ''
  
  return date.toISOString()
}